import { getIn } from "formik";
import { FGSelectInput, IFGSelectInputProps, useFGContext } from "nsitools-react";
import * as React from "react";

import { FunctionReferentialItemDto, ReferentialItemDto } from "../../api";
import { useCommonHooks, useFunctionRef } from "../../hooks";
import { ETLCodes } from "../../locales";
import { nameof } from "../../utils";

export interface IFunctionSelectProps extends IFGSelectInputProps<any> {
  name: string;
  label?: string;
  selectedFunctionIds?: number[];
  onFunctionSelected?: (func: FunctionReferentialItemDto | undefined) => void;
}

export const FunctionSelect: React.FunctionComponent<IFunctionSelectProps> = ({
  name,
  label,
  selectedFunctionIds,
  onFunctionSelected,
  ...fgSelectInputProps
}) => {
  const { t } = useCommonHooks();
  const { functions, functionsLoading } = useFunctionRef(selectedFunctionIds);
  const { formik } = useFGContext();

  const currentValue = getIn(formik?.values, name);
  const prevValueRef = React.useRef(currentValue);

  const computedFunctions = React.useMemo(() => {
    if (!functions) return [];
    const cf = [...functions];
    cf.unshift(
      new FunctionReferentialItemDto({
        categoryId: null,
        idValue: null,
        description: "",
        displayValue: t(ETLCodes.MissingFunction),
      })
    );
    return cf;
  }, [functions, t]);

  React.useEffect(() => {
    if (prevValueRef.current !== currentValue && onFunctionSelected) {
      const selectedFunc = computedFunctions.find(
        (f) =>
          f.idValue === currentValue ||
          (f.idValue != null &&
            currentValue != null &&
            +f.idValue === +currentValue)
      );
      onFunctionSelected(selectedFunc);
    }
    prevValueRef.current = currentValue;
  }, [currentValue, computedFunctions, onFunctionSelected]);

  return (
    <FGSelectInput
      noLabel={!label}
      label={label}
      name={name}
      items={computedFunctions}
      loading={functionsLoading}
      displayField={nameof<ReferentialItemDto>("displayValue")}
      valueField={nameof<ReferentialItemDto>("idValue")}
      isFilterable={true}
      {...fgSelectInputProps}
    />
  );
};
